// 1. Framework / library imports
import React, { useCallback } from "react";
import _ from "lodash";

// 2. Helpers
import { classNames } from "helpers";

// 3. UI Components
import { CheckBox } from "ui/components/CheckBox";

// 4. Local Components
import TableRowChild from "./TableRowChild";

// 5. Local Types
import { Column, ColumnTypes, TableRowProps } from "../types";


function resolveImageUrl(url: string[] | string) {
  if (Array.isArray(url) && url.length > 0) {
    return url[0];
  } else if (Array.isArray(url) && url.length === 0) {
    return null;
  }
  return url;
}

const TableRow: React.FC<TableRowProps> = (props) => {
  const {
    row,
    columns,
    selectableRows = true,
    selectedRows,
    onRowClick,
    onEventClick,
    onSelectCheckboxClick,
    disableSelection,
    columnsChild,
    CustomTableRowChild,
    childTableTitle,
    rowClassName,
  } = props;

  const [showChildRows, setShowChildRows] = React.useState(false);

  const resolveTableCellBody = (column: Column) => {
    switch (column.type) {
      case ColumnTypes.image: {
        return resolveImageUrl(_.get(row, column.field)) ? (
          <img
            src={_.get(row, column.field)}
            alt=""
            style={{ minWidth: "60px", maxWidth: "60px" }}
          />
        ) : (
          <div>No Image</div>
        );
      }
      case ColumnTypes.boolean: {
        return <CheckBox disabled={true} checked={_.get(row, column.field)} />;
      }
      case ColumnTypes.button: {
        return <div>Button</div>;
      }
      case ColumnTypes.string: {
        return _.get(row, column.field);
      }
      case ColumnTypes.custom: {
        return column.render(row, onEventClick);
      }
      default: {
        return null;
      }
    }
  };

  const resolveToggleChildRows = () => {
    return (
      <>
        {row.childRows &&
          row.childRows.length > 0
          && (
            <td className="px-4 py-2 text-sm font-medium ">
              <span
                onClick={handleToggleClick}
                className="absolute right-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2  cursor-pointer w-6 h-6 bg-slate-100 rounded-full flex justify-center items-center hover:bg-slate-200"
              >
                <svg
                  className={`w-4 h-4 transform transition-transform duration-300 ${showChildRows ? "rotate-180" : "rotate-0"
                    }`}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </span>
            </td>
          )}
      </>
    );
  };

  const handleToggleClick = useCallback(
    (event: React.MouseEvent<unknown>) => {
      event.stopPropagation(); // Zaustavlja propagaciju klika na parent
      setShowChildRows(!showChildRows); // Mijenja stanje
    },
    [setShowChildRows, showChildRows]
  );

  const isRowSelected = useCallback(
    (id: number) => selectedRows.indexOf(id) !== -1,
    [selectedRows]
  );

  const handleChecboxSelectClick = useCallback(
    (rowId: number) => (event: React.MouseEvent<unknown>) => {
      event.stopPropagation();
      if (onSelectCheckboxClick) onSelectCheckboxClick(rowId);
    },
    [onSelectCheckboxClick]
  );

  const handleRowClick = useCallback(
    (rowId: string) => (event: React.MouseEvent<unknown>) => {
      event.stopPropagation();
      event.preventDefault();
      if (onRowClick) onRowClick(rowId);
    },
    [onSelectCheckboxClick]
  );


  return (
    <>
      <tr
        className={classNames(
          onRowClick ? "cursor-pointer" : "",
          "border border-slate-200 hover:bg-slate-50 relative text-slate-900 ",
          rowClassName?.(row)
        )}
      >
        {selectableRows && (
          <td className="w-10 text-center ">
            <div className="flex items-center justify-center">
              <CheckBox
                checked={isRowSelected(row.id)}
                onChange={handleChecboxSelectClick(row.id)}
                disabled={Boolean(disableSelection)}
              />
            </div>
          </td>
        )}
        {columns.map((column: Column, index: number) => (
          <td
            key={index}
            className={classNames("px-4 py-2 text-sm font-medium ")}
            onClick={handleRowClick(row.id)}
          >
            {resolveTableCellBody(column)}

          </td>
        ))}
        {resolveToggleChildRows()}
      </tr>
      <TableRowChild
        rows={row.childRows}
        columns={columnsChild ?? []}
        show={showChildRows}
        CustomTableRowChild={CustomTableRowChild}
        onRowClick={onRowClick}
        parentColumnsCount={columns.length}
        childTableTitle={childTableTitle}
      />
    </>
  );
};
export default TableRow;
